import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography as MuiTypography,
} from '@mui/material';
import { Visibility } from '@mui/icons-material';
import notificationService from '../../services/api/notificationService';

const NotificationsPage = () => {
  const [tabValue, setTabValue] = useState(0);
  const [emailLogs, setEmailLogs] = useState([]);
  const [whatsAppLogs, setWhatsAppLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue, statusFilter, searchTerm]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const filters = {
        status: statusFilter || undefined,
        search: searchTerm || undefined,
      };

      if (tabValue === 0) {
        const data = await notificationService.getEmailLogs(filters);
        setEmailLogs(data);
      } else {
        const data = await notificationService.getWhatsAppLogs(filters);
        setWhatsAppLogs(data);
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (log) => {
    try {
      let detail;
      if (tabValue === 0) {
        detail = await notificationService.getEmailLogById(log.id);
      } else {
        detail = await notificationService.getWhatsAppLogById(log.id);
      }
      setSelectedLog(detail);
      setDetailDialogOpen(true);
    } catch (error) {
      console.error('Error fetching log details:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'sent':
      case 'delivered':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <Container maxWidth="xl">
      <Typography variant="h4" component="h1" gutterBottom>
        Notification Logs
      </Typography>

      <Paper sx={{ mt: 2 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Email Logs" />
          <Tab label="WhatsApp Logs" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* Filters */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={tabValue === 0 ? 'Search by email...' : 'Search by phone...'}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Sent">Sent</MenuItem>
                  <MenuItem value="Delivered">Delivered</MenuItem>
                  <MenuItem value="Failed">Failed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {tabValue === 0 ? (
                      <>
                        <TableCell>To Email</TableCell>
                        <TableCell>Subject</TableCell>
                        <TableCell>Template</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Sent At</TableCell>
                        <TableCell>Created At</TableCell>
                        <TableCell>Actions</TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>To Phone</TableCell>
                        <TableCell>Message Preview</TableCell>
                        <TableCell>Template</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Sent At</TableCell>
                        <TableCell>Delivered At</TableCell>
                        <TableCell>Created At</TableCell>
                        <TableCell>Actions</TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tabValue === 0
                    ? emailLogs.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell>{log.toEmail}</TableCell>
                          <TableCell>{log.subject}</TableCell>
                          <TableCell>{log.templateName || 'N/A'}</TableCell>
                          <TableCell>
                            <Chip
                              label={log.status}
                              color={getStatusColor(log.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{formatDate(log.sentAt)}</TableCell>
                          <TableCell>{formatDate(log.createdAt)}</TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              startIcon={<Visibility />}
                              onClick={() => handleViewDetails(log)}
                            >
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    : whatsAppLogs.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell>{log.toPhone}</TableCell>
                          <TableCell>
                            {log.message?.substring(0, 50)}
                            {log.message?.length > 50 ? '...' : ''}
                          </TableCell>
                          <TableCell>{log.templateName || 'N/A'}</TableCell>
                          <TableCell>
                            <Chip
                              label={log.status}
                              color={getStatusColor(log.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{formatDate(log.sentAt)}</TableCell>
                          <TableCell>{formatDate(log.deliveredAt)}</TableCell>
                          <TableCell>{formatDate(log.createdAt)}</TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              startIcon={<Visibility />}
                              onClick={() => handleViewDetails(log)}
                            >
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {!loading && tabValue === 0 && emailLogs.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">No email logs found</Typography>
            </Box>
          )}

          {!loading && tabValue === 1 && whatsAppLogs.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">No WhatsApp logs found</Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {tabValue === 0 ? 'Email' : 'WhatsApp'} Log Details
        </DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <MuiTypography variant="body2" color="text.secondary">
                    {tabValue === 0 ? 'To Email' : 'To Phone'}
                  </MuiTypography>
                  <MuiTypography variant="body1" gutterBottom>
                    {tabValue === 0 ? selectedLog.toEmail : selectedLog.toPhone}
                  </MuiTypography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MuiTypography variant="body2" color="text.secondary">
                    Status
                  </MuiTypography>
                  <Chip
                    label={selectedLog.status}
                    color={getStatusColor(selectedLog.status)}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Grid>
                {tabValue === 0 && (
                  <Grid item xs={12}>
                    <MuiTypography variant="body2" color="text.secondary">
                      Subject
                    </MuiTypography>
                    <MuiTypography variant="body1" gutterBottom>
                      {selectedLog.subject}
                    </MuiTypography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <MuiTypography variant="body2" color="text.secondary">
                    {tabValue === 0 ? 'Body' : 'Message'}
                  </MuiTypography>
                  <Paper sx={{ p: 2, mt: 1, bgcolor: 'grey.50', maxHeight: 300, overflow: 'auto' }}>
                    <MuiTypography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                      {tabValue === 0 ? selectedLog.body : selectedLog.message}
                    </MuiTypography>
                  </Paper>
                </Grid>
                {selectedLog.templateName && (
                  <Grid item xs={12} sm={6}>
                    <MuiTypography variant="body2" color="text.secondary">
                      Template
                    </MuiTypography>
                    <MuiTypography variant="body1">
                      {selectedLog.templateName}
                    </MuiTypography>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <MuiTypography variant="body2" color="text.secondary">
                    Created At
                  </MuiTypography>
                  <MuiTypography variant="body1">
                    {formatDate(selectedLog.createdAt)}
                  </MuiTypography>
                </Grid>
                {selectedLog.sentAt && (
                  <Grid item xs={12} sm={6}>
                    <MuiTypography variant="body2" color="text.secondary">
                      Sent At
                    </MuiTypography>
                    <MuiTypography variant="body1">
                      {formatDate(selectedLog.sentAt)}
                    </MuiTypography>
                  </Grid>
                )}
                {tabValue === 1 && selectedLog.deliveredAt && (
                  <Grid item xs={12} sm={6}>
                    <MuiTypography variant="body2" color="text.secondary">
                      Delivered At
                    </MuiTypography>
                    <MuiTypography variant="body1">
                      {formatDate(selectedLog.deliveredAt)}
                    </MuiTypography>
                  </Grid>
                )}
                {selectedLog.errorMessage && (
                  <Grid item xs={12}>
                    <MuiTypography variant="body2" color="error">
                      Error Message
                    </MuiTypography>
                    <MuiTypography variant="body2" color="error" sx={{ mt: 0.5 }}>
                      {selectedLog.errorMessage}
                    </MuiTypography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default NotificationsPage;

